import { useQuery } from '@tanstack/react-query';
import serviceIntegrations from 'services/integrations';

/**
 * @typedef {Object} ProviderCredentialField
 * @property {string} name - Request key the value is submitted under
 * @property {string} label - Form label shown to the customer
 * @property {boolean} secret - Render masked; the stored value is never sent back
 * @property {string} placeholder - Example value for the empty input
 * @property {string} help - Hint telling the customer where to find the value
 */

/**
 * @typedef {Object} IncentiveProvider
 * @property {string} name - Machine name, also the payment method sent when paying
 * @property {string} label - Display name (e.g. "Tremendous")
 * @property {string} description - One-line tile copy
 * @property {string} logo - Logo asset filename
 * @property {string|null} help_url - Help-center article for the setup steps
 * @property {'oauth_redirect'|'credentials_form'} connect_strategy - Which connect control to render
 * @property {ProviderCredentialField[]} credential_fields - Inputs for a credentials_form provider
 * @property {string|null} oauth_authorize_path - API path that starts the redirect
 * @property {Object<string, string>} summary_fields - Stored config key => label, shown once connected
 * @property {string} target_label - What the provider calls a study's reward ("Campaign", "Reward")
 * @property {string[]|null} supported_currencies - Currencies it can pay in; null means no restriction
 * @property {boolean} funding_account_per_study - Whether each study picks which provider-side account pays it
 * @property {boolean} connected - Whether this account has it set up
 * @property {Object<string, string>} summary - Values for summary_fields; never a secret
 */

export const INCENTIVE_PROVIDERS_QUERY_KEY = ['incentive-providers'];

/**
 * Query hook for the incentive provider manifest.
 *
 * This is the single source the integrations tiles, the payment-method options and
 * the per-study reward selects all render from — nothing in the frontend names an
 * individual provider.
 *
 * @param {import('@tanstack/react-query').UseQueryOptions<IncentiveProvider[]>} [options] - Additional useQuery options
 * @returns {import('@tanstack/react-query').UseQueryResult<IncentiveProvider[]>}
 */
export function useIncentiveProvidersQuery(options = {}) {
    return useQuery({
        queryKey: INCENTIVE_PROVIDERS_QUERY_KEY,
        queryFn: () => serviceIntegrations.getIncentiveProviders(),
        ...options
    });
}
